import React, { useMemo, useRef, useState } from 'react';
import { Document as PdfDocument, Page } from 'react-pdf';
import { Box, Button, Spinner, Text, VStack } from '@chakra-ui/react';
import { PdfFileSource } from '@/hooks/viewers/usePdfViewer.ts';
import { PageNumberReasonChange } from '@/hooks/viewers/usePdfViewer.ts';

export const PDF_PAGE_SIDEBAR_DEFAULT_WIDTH = 144;
const PDF_PAGE_SIDEBAR_MIN_WIDTH = 120;
const PDF_PAGE_SIDEBAR_MAX_WIDTH = 260;
const PDF_PAGE_SIDEBAR_HORIZONTAL_PADDING = 24;
const PDF_PAGE_THUMBNAIL_INSET = 10;
const PDF_PAGE_SIDEBAR_KEYBOARD_STEP = 12;

interface PdfPageSidebarProps {
	isOpen: boolean;
	width: number;
	pdfOptions: PdfFileSource;
	numPages: number;
	pageNumber: number;
	loading: boolean;
	rotate?: number;
	onPageChange: (page: number, reason: PageNumberReasonChange) => void;
	onWidthChange: (width: number) => void;
}

const PdfPageSidebar: React.FC<PdfPageSidebarProps> = (props: PdfPageSidebarProps) => {
	const { isOpen, width, pdfOptions, numPages, pageNumber, loading, rotate, onPageChange, onWidthChange } = props;
	const dragStartRef = useRef<{ x: number; width: number } | null>(null);
	const [committedThumbnailWidth, setCommittedThumbnailWidth] = useState(() =>
		Math.max(72, PDF_PAGE_SIDEBAR_DEFAULT_WIDTH - PDF_PAGE_SIDEBAR_HORIZONTAL_PADDING - PDF_PAGE_THUMBNAIL_INSET),
	);
	const [isResizing, setIsResizing] = useState(false);
	const memoizedPdfOptions = useMemo(
		() => pdfOptions,
		[
			'data' in pdfOptions ? pdfOptions.data : pdfOptions.url,
			'withCredentials' in pdfOptions ? pdfOptions.withCredentials : undefined,
		],
	);
	const liveThumbnailWidth = Math.max(72, width - PDF_PAGE_SIDEBAR_HORIZONTAL_PADDING - PDF_PAGE_THUMBNAIL_INSET);
	const thumbnailWidth = isResizing ? committedThumbnailWidth : liveThumbnailWidth;

	const setClampedWidth = (nextWidth: number) => {
		onWidthChange(Math.min(PDF_PAGE_SIDEBAR_MAX_WIDTH, Math.max(PDF_PAGE_SIDEBAR_MIN_WIDTH, nextWidth)));
	};

	const resizeSidebar = (event: React.PointerEvent<HTMLDivElement>) => {
		if (!dragStartRef.current) return;
		const nextWidth = dragStartRef.current.width + event.clientX - dragStartRef.current.x;
		setClampedWidth(nextWidth);
	};

	const startResize = (event: React.PointerEvent<HTMLDivElement>) => {
		event.preventDefault();
		dragStartRef.current = { x: event.clientX, width };
		setIsResizing(true);
		event.currentTarget.setPointerCapture(event.pointerId);
	};

	const stopResize = (event: React.PointerEvent<HTMLDivElement>) => {
		setCommittedThumbnailWidth(liveThumbnailWidth);
		dragStartRef.current = null;
		setIsResizing(false);
		event.currentTarget.releasePointerCapture(event.pointerId);
	};

	const handleResizeKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
		if (event.key === 'ArrowLeft') {
			event.preventDefault();
			setClampedWidth(width - PDF_PAGE_SIDEBAR_KEYBOARD_STEP);
		}
		if (event.key === 'ArrowRight') {
			event.preventDefault();
			setClampedWidth(width + PDF_PAGE_SIDEBAR_KEYBOARD_STEP);
		}
	};

	return (
		<Box
			w={isOpen ? `${width}px` : 0}
			flexShrink={0}
			bg="blackAlpha.500"
			borderRightWidth={isOpen ? '1px' : 0}
			borderColor="whiteAlpha.200"
			overflow="hidden"
			overflowY={isOpen ? 'auto' : 'hidden'}
			px={isOpen ? 3 : 0}
			py={4}
			position="relative"
			userSelect={isResizing ? 'none' : undefined}
			transition={isResizing ? 'none' : 'width 180ms ease, padding 180ms ease, border-width 180ms ease'}
		>
			<Box
				w="full"
				opacity={isOpen ? 1 : 0}
				transition="opacity 120ms ease"
				pointerEvents={isOpen ? 'auto' : 'none'}
			>
				<Box mb={3} minH="24px" display="flex" justifyContent="center" alignItems="center">
					<Text color="whiteAlpha.800" textStyle="micro.strong" textAlign="center">
						Pages
					</Text>
				</Box>
				{loading ? (
					<Box display="flex" justifyContent="center" py={6}>
						<Spinner size="sm" color="whiteAlpha.800" />
					</Box>
				) : (
					<PdfDocument file={memoizedPdfOptions} loading="">
						<VStack gap={3} align="stretch">
							{Array.from({ length: numPages }, (_, index) => {
								const page = index + 1;
								const isActive = page === pageNumber;
								return (
									<Button
										key={page}
										type="button"
										variant="plain"
										h="auto"
										p={1}
										borderRadius="md"
										borderWidth="1px"
										borderColor={isActive ? 'whiteAlpha.700' : 'whiteAlpha.200'}
										bg={isActive ? 'whiteAlpha.200' : 'transparent'}
										color="white"
										display="flex"
										flexDirection="column"
										gap={1.5}
										_hover={{ bg: 'whiteAlpha.200' }}
										onClick={() => onPageChange(page, 'control')}
									>
										<Box overflow="hidden" borderRadius="sm" bg="white" boxShadow="sm">
											<Page
												pageNumber={page}
												width={thumbnailWidth}
												rotate={rotate}
												renderTextLayer={false}
												renderAnnotationLayer={false}
											/>
										</Box>
										<Text as="span" textStyle="label.sm">
											{page}
										</Text>
									</Button>
								);
							})}
						</VStack>
					</PdfDocument>
				)}
			</Box>
			{isOpen && (
				<Box
					role="separator"
					aria-label="Resize page sidebar"
					aria-orientation="vertical"
					aria-valuemin={PDF_PAGE_SIDEBAR_MIN_WIDTH}
					aria-valuemax={PDF_PAGE_SIDEBAR_MAX_WIDTH}
					aria-valuenow={Math.round(width)}
					tabIndex={0}
					position="absolute"
					top={0}
					right={0}
					bottom={0}
					w="6px"
					cursor="col-resize"
					_hover={{ bg: 'whiteAlpha.200' }}
					_focusVisible={{ bg: 'whiteAlpha.300', outline: '2px solid', outlineColor: 'whiteAlpha.700' }}
					onPointerDown={startResize}
					onPointerMove={resizeSidebar}
					onPointerUp={stopResize}
					onPointerCancel={stopResize}
					onKeyDown={handleResizeKeyDown}
				/>
			)}
		</Box>
	);
};

export default PdfPageSidebar;
